import { useMemo, useState } from 'react'
import { Box, Dialog } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import TuneIcon from '@mui/icons-material/Tune'
import {
  DayHeader,
  pct,
  TOTAL_HOURS,
  WeekTrack,
} from './RulesWorkbench.timeline'
import {
  FONT_SANS,
  PillButton,
  PillToggle,
  RADII,
  SectionLabel,
  TOKENS,
  TYPE,
} from './SchedulerDesign'

/**
 * Bulk-editing a capacity profile.
 *
 * A profile is 168 cells per resource, and the thing a planner is actually
 * describing is almost never 168 independent numbers — it is a shift pattern:
 * "the crew is 2 on nights", "this line is off at weekends", "maintenance
 * Thursday 22:00-06:00". A single start/end range expresses the third and
 * turns the first two into seven separate edits, which is most of the typing
 * problem left unsolved. Hence the four modes below.
 */
export type BulkPattern =
  /** Every hour of the week. */
  | { mode: 'week' }
  /** One contiguous block of week-hours, `[from, to)`. */
  | { mode: 'range'; from: number; to: number }
  /** A time of day, every day. `22:00-06:00` is one action, not seven. */
  | { mode: 'daily'; fromHour: number; toHour: number }
  /** A time of day, on chosen days — weekdays-only shifts. */
  | { mode: 'days'; days: boolean[]; fromHour: number; toHour: number }

type Mode = BulkPattern['mode']

const MODE_LABELS: { mode: Mode; label: string; hint: string }[] = [
  { mode: 'week', label: 'Whole week', hint: 'Every hour of the week.' },
  { mode: 'range', label: 'Range', hint: 'One block of week-hours.' },
  {
    mode: 'daily',
    label: 'Every day',
    hint: 'A time of day, repeated on all seven days.',
  },
  {
    mode: 'days',
    label: 'On days',
    hint: 'A time of day, on the days you pick.',
  },
]

/**
 * The week-hours a pattern covers, sorted and free of duplicates.
 *
 * **Windows that cross midnight wrap**, which is the whole point of the
 * `daily`/`days` modes: `22:00-06:00` means 22:00 until 06:00 *the next
 * morning*, so it contributes hours 22, 23 of its own day and 0-5 of the next.
 * A window on Sunday wraps round to Monday — the profile describes a repeating
 * week, so that is the consistent reading, and the dialog's live count shows
 * exactly how many hours were selected so the wrap is never a surprise.
 *
 * Exported and pure so it can be checked without a browser; the Portal has no
 * test suite (`npm test` is a stub), so this is the part worth being able to
 * exercise directly.
 */
export const hoursForPattern = (p: BulkPattern): number[] => {
  const out = new Set<number>()
  const addTimeOfDay = (day: number, from: number, to: number) => {
    // `from === to` reads as a full 24 hours rather than nothing: a planner
    // who types the same value twice means "all day", never "no hours".
    const span = from < to ? to - from : 24 - from + to || 24
    for (let i = 0; i < span; i++) {
      out.add((day * 24 + from + i + TOTAL_HOURS) % TOTAL_HOURS)
    }
  }

  switch (p.mode) {
    case 'week':
      for (let h = 0; h < TOTAL_HOURS; h++) out.add(h)
      break
    case 'range': {
      const lo = Math.max(0, Math.min(p.from, p.to))
      const hi = Math.min(TOTAL_HOURS, Math.max(p.from, p.to))
      for (let h = lo; h < hi; h++) out.add(h)
      break
    }
    case 'daily':
      for (let d = 0; d < 7; d++) addTimeOfDay(d, p.fromHour, p.toHour)
      break
    case 'days':
      p.days.forEach((on, d) => {
        if (on) addTimeOfDay(d, p.fromHour, p.toHour)
      })
      break
  }
  return [...out].sort((a, b) => a - b)
}

type Props = {
  open: boolean
  onClose: () => void
  /** Display names of the resources the edit will be applied to. */
  resources: string[]
  /** Hours to write, and what to write. `null` clears back to inheriting. */
  onApply: (hours: number[], value: number | null) => void
}

/** Bare numeric input in the house style — the MUI `TextField` chrome is much
 *  heavier than anything else on these dialogs. */
const NumField = ({
  label,
  value,
  onChange,
  max,
  width = 74,
  disabled,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  max?: number
  width?: number
  disabled?: boolean
}) => (
  <Box>
    <Box sx={{ ...TYPE.micro, mb: '3px' }}>{label}</Box>
    <Box
      component="input"
      type="number"
      value={value}
      disabled={disabled}
      min={0}
      max={max}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        onChange(e.target.value)
      }
      sx={{
        width,
        height: 28,
        px: '8px',
        border: `1px solid ${TOKENS.line300}`,
        borderRadius: RADII.md,
        background: disabled ? TOKENS.surface50 : TOKENS.surface0,
        color: disabled ? TOKENS.ink300 : TOKENS.ink900,
        ...TYPE.data,
        '&:focus': {
          outline: 'none',
          borderColor: TOKENS.brandBlue,
          boxShadow: `0 0 0 2px ${TOKENS.blue50}`,
        },
      }}
    />
  </Box>
)

/** A surface0 panel on the surface50 body — the dialog's grouping unit. */
const Panel = ({
  children,
  sx,
}: {
  children: React.ReactNode
  sx?: object
}) => (
  <Box
    sx={{
      bgcolor: TOKENS.surface0,
      border: `1px solid ${TOKENS.line200}`,
      borderRadius: RADII.md,
      p: '12px 14px',
      ...sx,
    }}
  >
    {children}
  </Box>
)

export const ResourceCapacityBulkEdit = ({
  open,
  onClose,
  resources,
  onApply,
}: Props) => {
  const [mode, setMode] = useState<Mode>('daily')
  const [from, setFrom] = useState('0')
  const [to, setTo] = useState('24')
  const [todFrom, setTodFrom] = useState('22')
  const [todTo, setTodTo] = useState('6')
  const [days, setDays] = useState<boolean[]>([
    true,
    true,
    true,
    true,
    true,
    false,
    false,
  ])
  const [value, setValue] = useState('0')
  const [clear, setClear] = useState(false)

  const num = (s: string, fallback = 0) => {
    const n = Number(s)
    return Number.isFinite(n) ? n : fallback
  }

  const pattern: BulkPattern = useMemo(() => {
    switch (mode) {
      case 'week':
        return { mode: 'week' }
      case 'range':
        return { mode: 'range', from: num(from), to: num(to) }
      case 'daily':
        return { mode: 'daily', fromHour: num(todFrom), toHour: num(todTo) }
      case 'days':
        return {
          mode: 'days',
          days,
          fromHour: num(todFrom),
          toHour: num(todTo),
        }
    }
  }, [mode, from, to, todFrom, todTo, days])

  const hours = useMemo(() => hoursForPattern(pattern), [pattern])
  const numeric = Number(value)
  const valueValid =
    clear || (value !== '' && Number.isFinite(numeric) && numeric >= 0)
  const canApply = hours.length > 0 && resources.length > 0 && valueValid

  // Contiguous bands, purely so the preview track can draw them.
  const bands = useMemo(() => {
    const out: [number, number][] = []
    hours.forEach((h) => {
      const last = out[out.length - 1]
      if (last && last[1] === h) last[1] = h + 1
      else out.push([h, h + 1])
    })
    return out
  }, [hours])

  const activeHint = MODE_LABELS.find((m) => m.mode === mode)?.hint ?? ''

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 'min(560px, 94vw)',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          border: `1px solid ${TOKENS.line300}`,
          borderRadius: RADII.md,
          overflow: 'hidden',
          m: '24px',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '11px',
          px: '18px',
          py: '15px',
          borderBottom: `1px solid ${TOKENS.line200}`,
          flexShrink: 0,
          bgcolor: TOKENS.surface0,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: RADII.sm,
            bgcolor: TOKENS.tealTint,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <TuneIcon sx={{ fontSize: 18, color: TOKENS.brandTeal }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: TOKENS.ink900,
              letterSpacing: '-0.01em',
              fontFamily: FONT_SANS,
            }}
          >
            Set capacity
          </Box>
          <Box
            sx={{
              fontSize: 11.5,
              color: TOKENS.ink400,
              fontFamily: FONT_SANS,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {resources.length} resource{resources.length === 1 ? '' : 's'}:{' '}
            {resources.slice(0, 4).join(', ')}
            {resources.length > 4 ? ` +${resources.length - 4} more` : ''}
          </Box>
        </Box>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            border: 'none',
            bgcolor: 'transparent',
            cursor: 'pointer',
            p: '4px',
            borderRadius: RADII.sm,
            display: 'flex',
            '&:hover': { bgcolor: TOKENS.surface50 },
          }}
        >
          <CloseIcon sx={{ fontSize: 18, color: TOKENS.ink400 }} />
        </Box>
      </Box>

      {/* Body */}
      <Box
        sx={{
          p: '14px 18px',
          bgcolor: TOKENS.surface50,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        {/* Mode — a segmented pill row rather than four stacked radios, which
            keeps the dialog one screen and matches the week selector the
            planner has just used on this tab. */}
        <Panel>
          <SectionLabel sx={{ mb: '8px' }}>Apply to</SectionLabel>
          <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {MODE_LABELS.map((m) => (
              <PillToggle
                key={m.mode}
                label={m.label}
                on={mode === m.mode}
                onClick={() => setMode(m.mode)}
                title={m.hint}
              />
            ))}
          </Box>
          <Box sx={{ ...TYPE.small, color: TOKENS.ink400, mt: '8px' }}>
            {activeHint}
          </Box>

          {(mode === 'range' || mode === 'daily' || mode === 'days') && (
            <Box
              sx={{
                mt: '12px',
                pt: '12px',
                borderTop: `1px solid ${TOKENS.line100}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              {mode === 'days' && (
                <Box>
                  <Box sx={{ ...TYPE.micro, mb: '4px' }}>Days</Box>
                  <Box sx={{ display: 'flex', gap: '4px' }}>
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(
                      (d, i) => (
                        <PillToggle
                          key={d}
                          label={d}
                          on={days[i]}
                          onClick={() =>
                            setDays((prev) =>
                              prev.map((v, j) => (j === i ? !v : v))
                            )
                          }
                        />
                      )
                    )}
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                {mode === 'range' ? (
                  <>
                    <NumField
                      label="From hour"
                      value={from}
                      onChange={setFrom}
                      max={TOTAL_HOURS}
                    />
                    <Box
                      sx={{ ...TYPE.small, color: TOKENS.ink400, pb: '7px' }}
                    >
                      to
                    </Box>
                    <NumField
                      label="To hour"
                      value={to}
                      onChange={setTo}
                      max={TOTAL_HOURS}
                    />
                    <Box
                      sx={{ ...TYPE.small, color: TOKENS.ink400, pb: '7px' }}
                    >
                      of 168
                    </Box>
                  </>
                ) : (
                  <>
                    <NumField
                      label="From"
                      value={todFrom}
                      onChange={setTodFrom}
                      max={23}
                    />
                    <Box
                      sx={{ ...TYPE.small, color: TOKENS.ink400, pb: '7px' }}
                    >
                      to
                    </Box>
                    <NumField
                      label="To"
                      value={todTo}
                      onChange={setTodTo}
                      max={24}
                    />
                    <Box
                      sx={{ ...TYPE.small, color: TOKENS.ink400, pb: '7px' }}
                    >
                      o&#8217;clock
                      {num(todFrom) > num(todTo) ? ' — crosses midnight' : ''}
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}
        </Panel>

        {/* What the pattern actually selected. With 168 cells per resource,
            applying blind is how a planner wipes a week by accident — so the
            exact hours are drawn and counted before anything is written. */}
        <Panel>
          <SectionLabel sx={{ mb: '8px' }}>Selected hours</SectionLabel>
          <DayHeader />
          <WeekTrack height={20} radius={2}>
            {bands.map(([s, e], i) => (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  left: `${pct(s)}%`,
                  width: `${pct(e - s)}%`,
                  top: 0,
                  bottom: 0,
                  background: TOKENS.brandBlue,
                }}
              />
            ))}
          </WeekTrack>
        </Panel>

        {/* Value */}
        <Panel>
          <SectionLabel sx={{ mb: '8px' }}>Capacity</SectionLabel>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
            <NumField
              label="Value"
              value={value}
              onChange={setValue}
              disabled={clear}
            />
            <Box sx={{ pb: '2px' }}>
              <PillToggle
                label="Clear — inherit flat"
                on={clear}
                onClick={() => setClear((v) => !v)}
                title="Removes the authored value so the hour falls back to the resource's flat Constraint Amount."
              />
            </Box>
          </Box>
          {/* An authored 0 and a cleared cell are different things, and the
              difference is invisible once the dialog closes — so it is said
              here, where the choice is being made. */}
          {!clear && numeric === 0 && value !== '' && (
            <Box sx={{ ...TYPE.small, color: TOKENS.amberFg, mt: '8px' }}>
              0 means the resource is unavailable for those hours. To go back to
              inheriting the flat amount instead, use Clear.
            </Box>
          )}
        </Panel>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          px: '18px',
          py: '12px',
          borderTop: `1px solid ${TOKENS.line200}`,
          flexShrink: 0,
          bgcolor: TOKENS.surface0,
        }}
      >
        {/* The count is the safety net for a 168-cell write, so it sits in
            the footer beside Apply rather than buried in the body. */}
        <Box
          sx={{
            ...TYPE.small,
            color: hours.length === 0 ? TOKENS.ganttDowntime : TOKENS.ink500,
          }}
        >
          {hours.length === 0 ? (
            'No hours selected'
          ) : (
            <>
              <Box component="span" sx={{ ...TYPE.data, fontWeight: 700 }}>
                {hours.length * resources.length}
              </Box>{' '}
              cells — {hours.length}h × {resources.length} resource
              {resources.length === 1 ? '' : 's'},{' '}
              {clear ? 'cleared' : `set to ${value === '' ? '—' : value}`}
            </>
          )}
        </Box>
        <Box sx={{ flex: 1 }} />
        <PillButton kind="subtle" onClick={onClose}>
          Cancel
        </PillButton>
        <PillButton
          kind="primary"
          disabled={!canApply}
          icon={<CheckIcon />}
          onClick={() => {
            onApply(hours, clear ? null : numeric)
            onClose()
          }}
        >
          Apply
        </PillButton>
      </Box>
    </Dialog>
  )
}
